import { http } from './http';
import type { ApiSuccess } from './types';

export interface SystemSettings {
  retentionDays: number;
  storageFullThreshold: number;
  storageFullThresholdPercent: number;
  emergencyRetentionDays: number;
  sources: {
    retentionDays: 'database' | 'environment';
    storageFullThreshold: 'database' | 'environment';
    emergencyRetentionDays: 'database' | 'environment';
  };
}

export interface StorageVolume {
  path: string;
  label: string;
  storageTarget: 'local' | 'nas' | 'mixed';
  totalBytes: number;
  usedBytes: number;
  availableBytes: number;
  usageRatio: number;
  usagePercent: number;
  reachable: boolean;
  error?: string;
}

export interface StorageHealth {
  volumes: StorageVolume[];
  maxUsageRatio: number;
  maxUsagePercent: number;
  overThreshold: boolean;
  threshold: number;
  thresholdPercent: number;
}

export interface RetentionRunResult {
  mode: 'age' | 'emergency' | 'purge';
  retentionDaysApplied: number;
  deletedFiles: number;
  deletedBytes: number;
  skippedFiles: number;
  errors: string[];
  triggeredBy: 'scheduled' | 'capacity' | 'manual';
}

export interface SystemSettingsUpdate {
  retentionDays: number;
  storageFullThreshold: number;
  emergencyRetentionDays: number;
}

export const storageApi = {
  async getSettings(signal?: AbortSignal): Promise<SystemSettings> {
    const res = await http.get<ApiSuccess<SystemSettings>>('/api/storage/settings', { signal });
    return res.data.data;
  },

  async updateSettings(body: SystemSettingsUpdate): Promise<SystemSettings> {
    const res = await http.put<ApiSuccess<SystemSettings>>('/api/storage/settings', body);
    return res.data.data;
  },

  async getHealth(signal?: AbortSignal): Promise<StorageHealth> {
    const res = await http.get<ApiSuccess<StorageHealth>>('/api/storage/health', { signal });
    return res.data.data;
  },

  async runRetention(): Promise<RetentionRunResult> {
    const res = await http.post<ApiSuccess<RetentionRunResult>>('/api/storage/retention/run');
    return res.data.data;
  },
};
